import { z } from 'zod';
import { isToggleField, NON_DATA_FIELD_TYPES, sortFields } from './forms.js';
import { normalizeEgyptMobile } from './phone.js';
export const formValueSchema = z.union([
    z.string().max(10000),
    z.number().finite(),
    z.boolean(),
    z.array(z.string().max(200)).max(200),
    z.null(),
]);
/** Empty means "not answered". 0 and false ARE answers. */
export function isEmptyValue(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
function conditionHolds(condition, actual) {
    switch (condition.operator) {
        case 'EMPTY':
            return isEmptyValue(actual);
        case 'NOT_EMPTY':
            return !isEmptyValue(actual);
        case 'EQUALS':
        case 'NOT_EQUALS': {
            const expected = condition.value;
            const equal = Array.isArray(actual) ? typeof expected === 'string' && actual.includes(expected) : actual === expected;
            return condition.operator === 'EQUALS' ? equal : !equal;
        }
        case 'IN': {
            const list = Array.isArray(condition.value) ? condition.value : [];
            if (Array.isArray(actual))
                return actual.some((v) => list.includes(v));
            return typeof actual === 'string' && list.includes(actual);
        }
    }
}
/**
 * Values as the form means them: a toggle nobody touched is off (false), not
 * unanswered. Applied before visibility and validation so "reveal when on" and
 * reporting see the same answer on the phone and on the server.
 */
export function withFieldDefaults(fields, values) {
    let result = null;
    for (const field of fields) {
        if (isToggleField(field) && (values[field.fieldKey] === undefined || values[field.fieldKey] === null)) {
            result ??= { ...values };
            result[field.fieldKey] = false;
        }
    }
    return result ?? { ...values };
}
/**
 * Computes the set of visible field keys. A condition that refers to a hidden
 * field evaluates against an empty value, so visibility cascades.
 */
export function computeVisibleFieldKeys(fields, rawValues) {
    const values = withFieldDefaults(fields, rawValues);
    const visible = new Set();
    for (const field of sortFields(fields)) {
        const conditions = field.visibleWhen ?? [];
        const shown = conditions.every((condition) => conditionHolds(condition, visible.has(condition.fieldKey) ? values[condition.fieldKey] : undefined));
        if (shown)
            visible.add(field.fieldKey);
    }
    return visible;
}
/**
 * The options a field offers for the current answers: for a dependent field,
 * those listed under the parent's chosen option (none while the parent is
 * empty); for any other choice field, all of them.
 */
export function availableOptions(field, values) {
    const options = field.options ?? [];
    if (!field.dependsOn)
        return options;
    const parentValue = values[field.dependsOn.fieldKey];
    if (typeof parentValue !== 'string' || parentValue === '')
        return [];
    const allowed = new Set(field.dependsOn.optionsByParent[parentValue] ?? []);
    return options.filter((o) => allowed.has(o.value));
}
export const FORM_VALUE_ERROR_CODES = [
    'REQUIRED',
    'UNKNOWN_FIELD',
    'INVALID_TYPE',
    'INVALID_OPTION',
    'TOO_SHORT',
    'TOO_LONG',
    'WRONG_LENGTH',
    'TOO_SMALL',
    'TOO_LARGE',
    'PATTERN_MISMATCH',
    'INVALID_CHARACTERS',
    'INVALID_PHONE',
    'TOO_FEW_ITEMS',
    'TOO_MANY_ITEMS',
    /** A dependent field's value is an option, but not one offered for the parent's chosen option. */
    'OPTION_NOT_FOR_PARENT',
];
const CHARACTER_PATTERNS = {
    NUMERIC_ONLY: /^[0-9]+$/,
    ALPHANUMERIC: /^[\p{L}\p{N}]+$/u,
    NO_SPECIAL_CHARS: /^[\p{L}\p{N}\s]+$/u,
};
function validateTextLike(field, value, push) {
    const rules = field.validationRules ?? {};
    const length = [...value].length;
    if (rules.exactLength != null && length !== rules.exactLength) {
        push({ code: 'WRONG_LENGTH', message: `Must be exactly ${rules.exactLength} characters`, params: { length: rules.exactLength } });
    }
    if (rules.minLength != null && length < rules.minLength) {
        push({ code: 'TOO_SHORT', message: `Must be at least ${rules.minLength} characters`, params: { min: rules.minLength } });
    }
    if (rules.maxLength != null && length > rules.maxLength) {
        push({ code: 'TOO_LONG', message: `Must be at most ${rules.maxLength} characters`, params: { max: rules.maxLength } });
    }
    const restriction = rules.characterRestriction;
    if (restriction && restriction !== 'ALL' && !CHARACTER_PATTERNS[restriction]?.test(value)) {
        push({ code: 'INVALID_CHARACTERS', message: rules.errorMessage ?? 'Contains characters that are not allowed' });
    }
    if (rules.pattern && !new RegExp(rules.pattern, 'u').test(value)) {
        push({ code: 'PATTERN_MISMATCH', message: rules.errorMessage ?? 'Invalid format' });
    }
}
function validateNumber(field, value, push) {
    const rules = field.validationRules ?? {};
    if (rules.min != null && value < rules.min) {
        push({ code: 'TOO_SMALL', message: `Must be at least ${rules.min}`, params: { min: rules.min } });
    }
    if (rules.max != null && value > rules.max) {
        push({ code: 'TOO_LARGE', message: `Must be at most ${rules.max}`, params: { max: rules.max } });
    }
}
function validateCount(field, count, push) {
    const rules = field.validationRules ?? {};
    if (rules.minItems != null && count > 0 && count < rules.minItems) {
        push({ code: 'TOO_FEW_ITEMS', message: `Select at least ${rules.minItems}`, params: { min: rules.minItems } });
    }
    if (rules.maxItems != null && count > rules.maxItems) {
        push({ code: 'TOO_MANY_ITEMS', message: `Select at most ${rules.maxItems}`, params: { max: rules.maxItems } });
    }
}
/**
 * Validates submitted values against an exact form version. Shared by the
 * server (authoritative), the mobile app and the web preview.
 */
export function validateFormValues(fields, submittedValues, context = {}) {
    const errors = [];
    const values = {};
    const byKey = new Map(fields.map((f) => [f.fieldKey, f]));
    const rawValues = withFieldDefaults(fields, submittedValues);
    if (context.rejectUnknownFields) {
        for (const key of Object.keys(submittedValues)) {
            const field = byKey.get(key);
            if (!field || NON_DATA_FIELD_TYPES.includes(field.fieldType) || (context.fieldKeys && !context.fieldKeys.has(key))) {
                errors.push({ fieldKey: key, code: 'UNKNOWN_FIELD', message: `"${key}" is not a field of this form version` });
            }
        }
    }
    const typedValues = {};
    for (const [key, value] of Object.entries(rawValues)) {
        const parsed = formValueSchema.safeParse(value);
        if (parsed.success)
            typedValues[key] = parsed.data;
    }
    const visible = computeVisibleFieldKeys(fields, typedValues);
    /** A dependent field is checked against what its parent offers; a hidden parent offers nothing. */
    const offered = (field) => new Set(availableOptions(field, field.dependsOn && !visible.has(field.dependsOn.fieldKey) ? {} : typedValues).map((o) => o.value));
    const parentMismatch = (field) => ({
        code: 'OPTION_NOT_FOR_PARENT',
        message: `Not an option for the chosen "${field.dependsOn?.fieldKey}"`,
        params: { parent: field.dependsOn?.fieldKey ?? '' },
    });
    for (const field of sortFields(fields)) {
        if (!visible.has(field.fieldKey))
            continue;
        if (context.fieldKeys && !context.fieldKeys.has(field.fieldKey))
            continue;
        const push = (e) => errors.push({ fieldKey: field.fieldKey, ...e });
        switch (field.fieldType) {
            case 'ATTACHMENT_IMAGE': {
                const count = context.attachmentCounts?.[field.fieldKey] ?? 0;
                if (field.isRequired && count === 0)
                    push({ code: 'REQUIRED', message: 'At least one photo is required' });
                validateCount(field, count, push);
                continue;
            }
            case 'BRAND_SELECT':
                if (field.isRequired && !context.hasBrand)
                    push({ code: 'REQUIRED', message: 'Merchant is required' });
                continue;
            case 'BRANCH_SELECT':
                if (field.isRequired && !context.hasBranch)
                    push({ code: 'REQUIRED', message: 'Branch is required' });
                continue;
            default:
                break;
        }
        const raw = rawValues[field.fieldKey];
        const manualCount = field.fieldType === 'MULTI_SELECT' || field.fieldType === 'SELECT' ? (context.manualOptionCounts?.[field.fieldKey] ?? 0) : 0;
        if (field.fieldType === 'SELECT' && (manualCount > 1 || (manualCount === 1 && !isEmptyValue(raw)))) {
            // One project per block: a typed name replaces the choice, it never sits beside one.
            push({ code: 'TOO_MANY_ITEMS', message: 'Choose one option or type one name, not both', params: { max: 1 } });
            continue;
        }
        if (isEmptyValue(raw)) {
            if (manualCount > 0) {
                // Only typed entries: they fill the field (and its item limits); no listed option is chosen.
                if (field.fieldType === 'MULTI_SELECT') {
                    validateCount(field, manualCount, push);
                    values[field.fieldKey] = [];
                }
                continue;
            }
            if (field.isRequired)
                push({ code: 'REQUIRED', message: 'This field is required' });
            continue;
        }
        switch (field.fieldType) {
            case 'TEXT':
            case 'TEXTAREA': {
                if (typeof raw !== 'string') {
                    push({ code: 'INVALID_TYPE', message: 'Must be text' });
                    break;
                }
                const text = raw.trim();
                validateTextLike(field, text, push);
                values[field.fieldKey] = text;
                break;
            }
            case 'PHONE': {
                if (typeof raw !== 'string') {
                    push({ code: 'INVALID_TYPE', message: 'Must be text' });
                    break;
                }
                const normalized = normalizeEgyptMobile(raw);
                if (!normalized) {
                    push({
                        code: 'INVALID_PHONE',
                        message: field.validationRules?.errorMessage ?? 'Must be a valid Egyptian mobile number (01XXXXXXXXX)',
                    });
                    break;
                }
                values[field.fieldKey] = normalized;
                break;
            }
            case 'NUMBER':
            case 'CURRENCY': {
                if (typeof raw !== 'number' || !Number.isFinite(raw)) {
                    push({ code: 'INVALID_TYPE', message: 'Must be a number' });
                    break;
                }
                validateNumber(field, raw, push);
                values[field.fieldKey] = raw;
                break;
            }
            case 'BOOLEAN': {
                if (typeof raw !== 'boolean') {
                    push({ code: 'INVALID_TYPE', message: 'Must be yes or no' });
                    break;
                }
                values[field.fieldKey] = raw;
                break;
            }
            case 'SELECT': {
                if (typeof raw !== 'string') {
                    push({ code: 'INVALID_TYPE', message: 'Must be a single choice' });
                    break;
                }
                if (!field.options?.some((o) => o.value === raw)) {
                    push({ code: 'INVALID_OPTION', message: 'Not one of the allowed options' });
                    break;
                }
                if (field.dependsOn && !offered(field).has(raw)) {
                    push(parentMismatch(field));
                    break;
                }
                values[field.fieldKey] = raw;
                break;
            }
            case 'MULTI_SELECT': {
                if (!Array.isArray(raw) || raw.some((v) => typeof v !== 'string')) {
                    push({ code: 'INVALID_TYPE', message: 'Must be a list of choices' });
                    break;
                }
                const list = raw;
                const allowed = new Set(field.options?.map((o) => o.value) ?? []);
                if (list.some((v) => !allowed.has(v)) || new Set(list).size !== list.length) {
                    push({ code: 'INVALID_OPTION', message: 'Contains an option that is not allowed or is duplicated' });
                    break;
                }
                if (field.dependsOn) {
                    const forParent = offered(field);
                    if (list.some((v) => !forParent.has(v))) {
                        push(parentMismatch(field));
                        break;
                    }
                }
                validateCount(field, list.length + manualCount, push);
                values[field.fieldKey] = list;
                break;
            }
        }
    }
    return { errors, values };
}
//# sourceMappingURL=form-validation.js.map